<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import JuyangAiAPI from 'dashboard/api/juyangAi';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  assistantId: { type: Number, required: true },
  editable: { type: Boolean, default: false },
});

const { t } = useI18n();
const entries = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const editingId = ref(null);
const showForm = ref(false);
const form = reactive({ title: '', content: '', source_url: '' });

const formTitle = computed(() =>
  editingId.value
    ? t('JUYANG_AI.KNOWLEDGE.UPDATE')
    : t('JUYANG_AI.KNOWLEDGE.ADD')
);

const errorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  t('JUYANG_AI.ERROR');

const loadEntries = async () => {
  isLoading.value = true;
  try {
    const { data } = await JuyangAiAPI.getNested(
      props.assistantId,
      'knowledge_entries'
    );
    entries.value = data;
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  Object.assign(form, { title: '', content: '', source_url: '' });
  editingId.value = null;
  showForm.value = false;
};

const beginCreate = () => {
  resetForm();
  showForm.value = true;
};

const beginEdit = entry => {
  Object.assign(form, {
    title: entry.title,
    content: entry.content,
    source_url: entry.source_url || '',
  });
  editingId.value = entry.id;
  showForm.value = true;
};

const saveEntry = async () => {
  isSaving.value = true;
  const payload = {
    title: form.title.trim(),
    content: form.content.trim(),
    source_kind: form.source_url.trim() ? 'url' : 'manual',
    source_url: form.source_url.trim() || null,
  };

  try {
    if (editingId.value) {
      await JuyangAiAPI.updateNested(
        props.assistantId,
        'knowledge_entries',
        editingId.value,
        'knowledge_entry',
        payload
      );
      useAlert(t('JUYANG_AI.KNOWLEDGE.UPDATE_SUCCESS'));
    } else {
      await JuyangAiAPI.createNested(
        props.assistantId,
        'knowledge_entries',
        'knowledge_entry',
        payload
      );
      useAlert(t('JUYANG_AI.KNOWLEDGE.CREATE_SUCCESS'));
    }
    resetForm();
    await loadEntries();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const deleteEntry = async entry => {
  if (!window.confirm(t('JUYANG_AI.KNOWLEDGE.DELETE_CONFIRM'))) return;

  try {
    await JuyangAiAPI.deleteNested(
      props.assistantId,
      'knowledge_entries',
      entry.id
    );
    useAlert(t('JUYANG_AI.KNOWLEDGE.DELETE_SUCCESS'));
    await loadEntries();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const statusLabel = status => t(`JUYANG_AI.STATUS_${status.toUpperCase()}`);

watch(() => props.assistantId, loadEntries, { immediate: true });
</script>

<template>
  <section class="flex flex-col gap-5">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('JUYANG_AI.KNOWLEDGE.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('JUYANG_AI.KNOWLEDGE.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="editable"
        icon="i-lucide-plus"
        :label="t('JUYANG_AI.KNOWLEDGE.ADD')"
        size="sm"
        @click="beginCreate"
      />
    </div>

    <form
      v-if="showForm"
      class="flex flex-col gap-4 border-y border-n-weak py-5"
      @submit.prevent="saveEntry"
    >
      <h3 class="text-sm font-medium text-n-slate-12">{{ formTitle }}</h3>
      <Input
        v-model="form.title"
        :label="t('JUYANG_AI.KNOWLEDGE.ENTRY_TITLE')"
        required
      />
      <Input
        v-model="form.source_url"
        type="url"
        :label="t('JUYANG_AI.KNOWLEDGE.SOURCE_URL')"
      />
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('JUYANG_AI.KNOWLEDGE.CONTENT') }}
        <textarea
          v-model="form.content"
          class="reset-base min-h-48 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
          required
        />
      </label>
      <div class="flex justify-end gap-2">
        <Button
          type="button"
          color="slate"
          variant="ghost"
          :label="t('JUYANG_AI.CANCEL')"
          @click="resetForm"
        />
        <Button
          type="submit"
          icon="i-lucide-save"
          :label="isSaving ? t('JUYANG_AI.SAVING') : t('JUYANG_AI.SAVE')"
          :is-loading="isSaving"
        />
      </div>
    </form>

    <div v-if="isLoading" class="py-12 text-center text-sm text-n-slate-11">
      {{ t('JUYANG_AI.LOADING') }}
    </div>
    <div
      v-else-if="!entries.length"
      class="border-y border-n-weak py-12 text-center text-sm text-n-slate-11"
    >
      {{ t('JUYANG_AI.KNOWLEDGE.EMPTY') }}
    </div>
    <ul v-else class="m-0 flex list-none flex-col divide-y divide-n-weak p-0">
      <li
        v-for="entry in entries"
        :key="entry.id"
        class="flex items-start justify-between gap-5 py-4"
      >
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ entry.title }}
            </h3>
            <span class="text-xs text-n-slate-10">
              {{ statusLabel(entry.status) }}
            </span>
          </div>
          <p
            class="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-n-slate-11"
          >
            {{ entry.content }}
          </p>
        </div>
        <div v-if="editable" class="flex shrink-0 gap-1">
          <Button
            icon="i-lucide-pencil"
            color="slate"
            variant="ghost"
            size="sm"
            :title="t('JUYANG_AI.EDIT')"
            @click="beginEdit(entry)"
          />
          <Button
            icon="i-lucide-trash-2"
            color="ruby"
            variant="ghost"
            size="sm"
            :title="t('JUYANG_AI.DELETE')"
            @click="deleteEntry(entry)"
          />
        </div>
      </li>
    </ul>
  </section>
</template>
